// @ts-ignore - tutorService is a JS file, will be converted to TS later
import { tutorService } from "./tutorService";
import { db } from "@/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import type { TimeSlot, AvailabilitySlot, WeeklySlots } from "@/types/availability";
import {
  formatDateToString,
  parseTimeString,
  formatTimeDisplay,
  calculateDurationMinutes,
  formatDuration,
} from "@/utils/dateUtils";
import { logDebug, logError } from "@/utils/secureLogger";

// Simple event emitter for availability changes
class AvailabilityEventEmitter {
  private listeners: { [key: string]: Function[] } = {};

  on(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback: Function) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
  }

  emit(event: string, data?: any) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((callback) => callback(data));
  }
}

const availabilityEvents = new AvailabilityEventEmitter();

class AvailabilityService {
  private daysOfWeek: string[] = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  private availabilityCache: Map<string, { data: any[]; timestamp: number }> = new Map();
  private CACHE_DURATION = 10 * 1000; // 10 seconds cache for immediate booking updates
  private lastCleanupTime: Map<string, number> = new Map(); // Track last cleanup time per tutor
  private CLEANUP_INTERVAL = 2 * 60 * 1000; // 2 minutes between cleanups

  constructor() {
    // Constructor can be empty
  }

  /**
   * Check if a time slot is booked by checking both recurring availability blocks and date-specific blocks
   */
  private async isSlotBooked(
    tutorId: string,
    date: Date,
    startTime: string,
    endTime: string
  ): Promise<boolean> {
    try {
      // Create start and end datetime objects for comparison
      const [startHour, startMin] = startTime.split(":").map(Number);
      const [endHour, endMin] = endTime.split(":").map(Number);

      const slotStart = new Date(date);
      slotStart.setHours(startHour, startMin, 0, 0);

      const slotEnd = new Date(date);
      slotEnd.setHours(endHour, endMin, 0, 0);

      // Check for blocked slots that overlap with this time slot
      const dateString = formatDateToString(date);
      const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
      const blockedQuery = query(blockedSlotsRef, where("date", "==", dateString));

      const blockedSnapshot = await getDocs(blockedQuery);
      if (!blockedSnapshot.empty) {
        // Check if any blocked slot overlaps with this time slot
        const blockedSlots = blockedSnapshot.docs.map((doc) => doc.data());
        const hasOverlap = blockedSlots.some((slot) => {
          const [blockedStartHour, blockedStartMin] = slot.startTime.split(":").map(Number);
          const [blockedEndHour, blockedEndMin] = slot.endTime.split(":").map(Number);

          const blockedStart = new Date(date);
          blockedStart.setHours(blockedStartHour, blockedStartMin, 0, 0);

          const blockedEnd = new Date(date);
          blockedEnd.setHours(blockedEndHour, blockedEndMin, 0, 0);

          // Check for overlap: slot starts before blocked ends AND slot ends after blocked starts
          return slotStart < blockedEnd && slotEnd > blockedStart;
        });

        if (hasOverlap) {
          return true; // Slot is blocked
        }
      }

      // Check for recurring availability blocks (if any exist)
      const dayOfWeek = this.daysOfWeek[date.getDay()];
      const availabilityRef = collection(db, "users", tutorId, "availability");
      const availabilityQuery = query(
        availabilityRef,
        where("dayOfWeek", "==", dayOfWeek),
        where("startTime", "==", startTime),
        where("endTime", "==", endTime),
        where("isBlocked", "==", true)
      );

      const availabilitySnapshot = await getDocs(availabilityQuery);
      return !availabilitySnapshot.empty; // Blocked if any recurring blocks exist
    } catch (error) {
      logError(
        "Error checking if slot is booked",
        error instanceof Error ? error : new Error(String(error))
      );
      return false; // Default to available on error
    }
  }

  /**
   * Check if a time slot is booked using pre-fetched blocked slots (in-memory check)
   */
  private isSlotBookedInMemory(
    blockedSlots: any[],
    date: Date,
    startTime: string,
    endTime: string
  ): boolean {
    // Create start and end datetime objects for comparison
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);

    const slotStart = new Date(date);
    slotStart.setHours(startHour, startMin, 0, 0);

    const slotEnd = new Date(date);
    slotEnd.setHours(endHour, endMin, 0, 0);

    // Format the date to match the blocked slot date format
    const dateString = formatDateToString(date);

    // Check if there's a blocked slot that overlaps with this time slot
    return blockedSlots.some((slot) => {
      // Check if the blocked slot is for the same date
      if (slot.date !== dateString) {
        return false;
      }

      // Parse the blocked slot times (they are in "HH:MM" format)
      const [blockedStartHour, blockedStartMin] = slot.startTime.split(":").map(Number);
      const [blockedEndHour, blockedEndMin] = slot.endTime.split(":").map(Number);

      const blockedStart = new Date(date);
      blockedStart.setHours(blockedStartHour, blockedStartMin, 0, 0);

      const blockedEnd = new Date(date);
      blockedEnd.setHours(blockedEndHour, blockedEndMin, 0, 0);

      // Check for overlap: slot starts before blocked ends AND slot ends after blocked starts
      return slotStart < blockedEnd && slotEnd > blockedStart;
    });
  }

  /**
   * Get tutor availability with caching
   */
  private async getTutorAvailabilityCached(tutorId: string): Promise<any[]> {
    const cacheKey = `tutor_${tutorId}`;
    const cached = this.availabilityCache.get(cacheKey);
    const now = Date.now();

    // Return cached data if it's still valid
    if (cached && now - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    // Fetch fresh data from Firebase
    const availability = await tutorService.getTutorAvailability(tutorId);

    // Cache the data
    this.availabilityCache.set(cacheKey, {
      data: availability,
      timestamp: now,
    });

    return availability;
  }

  /**
   * Get all blocked slots for a specific week with caching
   */
  private async getBlockedSlotsForWeek(tutorId: string, startOfWeek: Date): Promise<any[]> {
    try {
      const startDateString = formatDateToString(startOfWeek);
      const cacheKey = `blocked_${tutorId}_${startDateString}`;
      const cached = this.availabilityCache.get(cacheKey);
      const now = Date.now();

      // Return cached data if it's still valid (shorter cache for blocked slots)
      if (cached && now - cached.timestamp < this.CACHE_DURATION / 2) {
        return cached.data;
      }

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      const endDateString = formatDateToString(endOfWeek);

      const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
      const q = query(
        blockedSlotsRef,
        where("date", ">=", startDateString),
        where("date", "<=", endDateString)
      );

      const snapshot = await getDocs(q);
      const blockedSlots = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Cache the blocked slots
      this.availabilityCache.set(cacheKey, {
        data: blockedSlots,
        timestamp: now,
      });

      return blockedSlots;
    } catch (error) {
      logError(
        "Error fetching blocked slots for week",
        error instanceof Error ? error : new Error(String(error))
      );
      return [];
    }
  }

  /**
   * Generate available time slots for a tutor for a given week
   */
  async generateWeeklySlots(tutorId: string, startOfWeek: Date): Promise<WeeklySlots> {
    try {
      // Clean up orphaned blocked slots periodically (not on every call to avoid performance issues)
      const now = Date.now();
      const lastCleanup = this.lastCleanupTime.get(tutorId) || 0;

      if (now - lastCleanup > this.CLEANUP_INTERVAL) {
        this.lastCleanupTime.set(tutorId, now);
        // Run cleanup in background, doesn't block the main flow
        this.cleanupOrphanedBlockedSlots(tutorId).catch((error) => {
          logError(
            "Background cleanup of orphaned slots failed",
            error instanceof Error ? error : new Error(String(error))
          );
        });
      }

      // Fetch tutor's availability from Firebase with caching
      const availability = await this.getTutorAvailabilityCached(tutorId);

      if (!availability || availability.length === 0) {
        return {}; // Return empty slots when no availability data
      }

      // Fetch all blocked slots for this week in one query
      const blockedSlots = await this.getBlockedSlotsForWeek(tutorId, startOfWeek);

      const weekSlots: WeeklySlots = {};

      // Generate slots for each day of the week in parallel for better performance
      const dayPromises = [];
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const currentDate = new Date(startOfWeek);
        currentDate.setDate(startOfWeek.getDate() + dayOffset);

        const dayKey = `${currentDate.getFullYear()}-${
          currentDate.getMonth() + 1
        }-${currentDate.getDate()}`;
        const dayOfWeekName = this.daysOfWeek[currentDate.getDay()];

        // Find availability for this day - filter out blocked slots
        const dayAvailability = availability.filter(
          (avail: any) => avail.dayOfWeek === dayOfWeekName && !avail.isBlocked
        );

        // Create promise for this day
        const dayPromise = this.generateDaySlots(
          dayAvailability,
          currentDate,
          availability,
          dayOfWeekName,
          blockedSlots
        ).then((slots) => ({ dayKey, slots }));

        dayPromises.push(dayPromise);
      }

      // Wait for all days to be processed in parallel
      const dayResults = await Promise.all(dayPromises);

      // Populate the weekSlots object
      dayResults.forEach(({ dayKey, slots }) => {
        weekSlots[dayKey] = slots;
      });

      return weekSlots;
    } catch (error) {
      logError(
        "Error generating weekly slots",
        error instanceof Error ? error : new Error(String(error))
      );
      // Return empty slots on error instead of default data
      return {};
    }
  }

  /**
   * Generate time slots for a specific day based on tutor's availability
   */
  async generateDaySlots(
    dayAvailability: AvailabilitySlot[],
    date: Date,
    allAvailability: any[],
    dayOfWeekName: string,
    blockedSlots: any[]
  ): Promise<TimeSlot[]> {
    const slots: TimeSlot[] = [];
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const currentHour = now.getHours();

    for (const availability of dayAvailability) {
      const startTime = availability.startTime; // e.g., "14:00"
      const endTime = availability.endTime; // e.g., "16:00"
      const mode = availability.mode; // e.g., "online"

      // Parse start and end times
      const [startHour, startMin] = startTime.split(":").map(Number);
      const [endHour, endMin] = endTime.split(":").map(Number);

      // Create availability slot showing start time only (industry standard)
      const startTimeFormatted = this.formatTime(startHour, startMin);
      const endTimeFormatted = this.formatTime(endHour, endMin);
      const durationHours = endHour - startHour;
      const durationMinutes = endMin - startMin + durationHours * 60;

      // Check if this time block is in the past (for today)
      // ✅ FIX: Compare full datetime including minutes, not just hour
      const slotStartDateTime = new Date(date);
      slotStartDateTime.setHours(startHour, startMin, 0, 0);
      const isPast = isToday && slotStartDateTime.getTime() <= now.getTime();

      // Check if this slot is booked using the pre-fetched blocked slots
      const isBooked = this.isSlotBookedInMemory(blockedSlots, date, startTime, endTime);

      // ✅ NEW: Check if slot is within 12 hours from now (cannot book slots less than 12 hours ahead)
      const slotDateTime = new Date(date);
      slotDateTime.setHours(startHour, startMin, 0, 0);
      const hoursUntilSlot = (slotDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
      const isWithin12Hours = hoursUntilSlot > 0 && hoursUntilSlot < 12;

      slots.push({
        time: startTimeFormatted, // Show only start time for cleaner look
        startTime: startTime,
        endTime: endTime,
        startTimeFormatted: startTimeFormatted,
        endTimeFormatted: endTimeFormatted,
        startHour: startHour,
        endHour: endHour,
        duration: durationMinutes,
        durationText:
          durationMinutes >= 60
            ? `${Math.floor(durationMinutes / 60)}h ${
                durationMinutes % 60 > 0 ? (durationMinutes % 60) + "m" : ""
              }`.trim()
            : `${durationMinutes}m`,
        fullTimeRange: `${startTimeFormatted} - ${endTimeFormatted}`,
        available: !isPast && !isBooked && !isWithin12Hours, // Available if not past, not booked, AND not within 12 hours
        isBooked: isBooked,
        mode: mode,
        isSelected: false,
        isAvailabilitySlot: true, // Flag to indicate this comes from availability data
      });
    }

    // Sort slots by start time and remove duplicates
    return slots
      .sort((a, b) => a.startHour - b.startHour)
      .filter((slot, index, arr) => index === 0 || slot.startTime !== arr[index - 1].startTime);
  }

  /**
   * Format hour and minute to display time
   */
  private formatTime(hour: number, minute: number = 0): string {
    return formatTimeDisplay(hour, minute);
  }

  /**
   * Mark a time slot as unavailable in the tutor's availability subcollection
   * This creates a date-specific block instead of blocking the recurring availability
   */
  async markSlotAsUnavailable(
    tutorId: string,
    date: Date,
    startTime: string,
    endTime: string,
    sessionId: string
  ): Promise<void> {
    try {
      const dateString = formatDateToString(date);
      const dayOfWeek = this.daysOfWeek[date.getDay()];

      // Instead of blocking the recurring availability, create a specific date block
      const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");

      // Create a new blocked slot document for this specific date and time
      const docData = {
        date: dateString,
        dayOfWeek: dayOfWeek,
        startTime: startTime,
        endTime: endTime,
        sessionId: sessionId,
        blockedAt: new Date(),
        reason: "session_booked",
      };

      const docRef = await addDoc(blockedSlotsRef, docData);

      // Clear cache for this tutor to ensure fresh data
      this.clearTutorCache(tutorId);

      // Also clear all cache to force immediate refresh across the app
      this.clearAllCache();

      // Emit availability change event to notify listening components
      availabilityEvents.emit("availabilityChanged", {
        tutorId,
        action: "slotBlocked",
        date: dateString,
        startTime,
        endTime,
        sessionId,
      });
    } catch (error) {
      logError(
        "Error marking slot as unavailable",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Mark a time slot as available again (when session is cancelled)
   */
  async markSlotAsAvailable(tutorId: string, sessionId: string): Promise<void> {
    try {
      const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
      const q = query(blockedSlotsRef, where("sessionId", "==", sessionId));

      const snapshot = await getDocs(q);

      // Delete the blocked slot documents
      const deletePromises = snapshot.docs.map((docSnapshot) =>
        deleteDoc(doc(db, "users", tutorId, "blockedSlots", docSnapshot.id))
      );

      await Promise.all(deletePromises);

      // Clear cache for this tutor to ensure fresh data
      this.clearTutorCache(tutorId);

      // Emit availability change event
      availabilityEvents.emit("availabilityChanged", {
        tutorId,
        action: "slotUnblocked",
        sessionId,
      });
    } catch (error) {
      logError(
        "Error marking slot as available",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Clear cache for a specific tutor
   */
  clearTutorCache(tutorId: string): void {
    const cacheKey = `tutor_${tutorId}`;
    this.availabilityCache.delete(cacheKey);
  }

  /**
   * Force refresh availability data for a tutor (bypasses cache)
   */
  async forceRefreshTutorAvailability(tutorId: string): Promise<any[]> {
    this.clearTutorCache(tutorId);
    return await this.getTutorAvailabilityCached(tutorId);
  }

  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    this.availabilityCache.clear();
  }

  /**
   * Listen to availability change events
   */
  onAvailabilityChanged(callback: Function) {
    availabilityEvents.on("availabilityChanged", callback);
  }

  /**
   * Remove availability change listener
   */
  offAvailabilityChanged(callback: Function) {
    availabilityEvents.off("availabilityChanged", callback);
  }

  /**
   * Clean up old blocked slots (older than 30 days)
   * This should be called periodically to keep the database clean
   */
  async cleanupOldBlockedSlots(tutorId: string): Promise<void> {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const cutoffDate = formatDateToString(thirtyDaysAgo);

      const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
      const q = query(blockedSlotsRef, where("date", "<", cutoffDate));

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const deletePromises = snapshot.docs.map((docSnapshot) =>
          deleteDoc(doc(db, "users", tutorId, "blockedSlots", docSnapshot.id))
        );

        await Promise.all(deletePromises);
      }
    } catch (error) {
      logError(
        "Error cleaning up old blocked slots",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Clean up orphaned blocked slots (where the session no longer exists)
   * This handles cases where sessions are manually deleted from Firebase
   */
  async cleanupOrphanedBlockedSlots(tutorId: string): Promise<number> {
    try {
      const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
      const snapshot = await getDocs(blockedSlotsRef);

      if (snapshot.empty) {
        return 0;
      }

      const orphanedSlots: string[] = [];

      // Check each blocked slot to see if its session still exists
      for (const docSnapshot of snapshot.docs) {
        const blockedSlot = docSnapshot.data();
        const sessionId = blockedSlot.sessionId;

        if (sessionId) {
          // Check if the session exists in the sessions collection
          const sessionsRef = collection(db, "sessions");
          const sessionQuery = query(sessionsRef, where("__name__", "==", sessionId));
          const sessionSnapshot = await getDocs(sessionQuery);

          // If session doesn't exist, mark this blocked slot as orphaned
          if (sessionSnapshot.empty) {
            orphanedSlots.push(docSnapshot.id);
          }
        }
      }

      // Delete all orphaned blocked slots
      if (orphanedSlots.length > 0) {
        const deletePromises = orphanedSlots.map((slotId) =>
          deleteDoc(doc(db, "users", tutorId, "blockedSlots", slotId))
        );

        await Promise.all(deletePromises);

        // Clear cache for this tutor to ensure fresh data
        this.clearTutorCache(tutorId);

        // Emit availability change event to notify listening components
        availabilityEvents.emit("availabilityChanged", {
          tutorId,
          action: "orphanedSlotsCleanup",
          count: orphanedSlots.length,
        });

        logInfo(`Cleaned up ${orphanedSlots.length} orphaned blocked slot(s) for tutor ${tutorId}`);
      }

      return orphanedSlots.length;
    } catch (error) {
      logError(
        "Error cleaning up orphaned blocked slots",
        error instanceof Error ? error : new Error(String(error))
      );
      return 0;
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let availabilityServiceInstance: AvailabilityService | null = null;

export function getAvailabilityService(): AvailabilityService {
  if (!availabilityServiceInstance) {
    availabilityServiceInstance = new AvailabilityService();
  }
  return availabilityServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const availabilityService = new Proxy({} as AvailabilityService, {
  get(target, prop) {
    return getAvailabilityService()[prop as keyof AvailabilityService];
  },
});
export default AvailabilityService;
